import { absoluteGradingEnabled } from "../../../../helpers/scheduleGrading";
import { calculateLaborGradeScore, buildGradeAlignedLaborScores } from "./ScheduleBusinessScorecard";
import React, { useEffect } from "react";
import { getActualComplianceWarnings } from "./actualComplianceWarnings";
import PersonCard from "./PersonCard";
import { getStoreBusinessDate, getStoreHoursNow } from "../../../../utilities/storeTime";
import {
  buildActualTimesByPublishedId,
  buildActualCards,
  buildCoverageOwners,
  buildCoveredPublishedIds,
  missedShiftHasElapsed as hasElapsed,
} from "./actualShiftTimes";
import { isManagerCancelledShift } from "./attendanceCoverage";
import DateCard2 from "./DateCard2";
import EditTeamButton2 from "./EditTeamButton2";
import PersonDayShiftDisplay from "./PersonDayShiftDisplay";
import PersonDayAvailabilityDisplay from "./PersonDayAvailabilityDisplay";
import SkeletonDisplay from "./SkeletonDisplay";
import { sum } from "d3";
import DailySchedule from "./daily_view/DailySchedule";
import MonthlySchedule from "./monthly_view/MonthlySchedule";
import { get_t_str } from "../../../../helpers/helpers";
import NoStoreTeamMemberDisplay from "./NoStoreTeamMembersDisplay";
import OpenShiftsDropdown, {
  OPEN_SHIFT_ROW_HEIGHT,
  OPEN_SHIFT_ROW_GAP,
  OPEN_SHIFT_VISIBLE_CAP,
} from "./OpenShiftsDropdown";
import EventsNotesRow from "./EventsNotesRow";
import FixedBottomMetricsBar from "./FixedBottomMetricsBar";
import WeeklySummaryCards from "./WeeklySummaryCards";
import { DEFAULT_CARD_ORDER } from "./summaryCardRegistry";
import { Search } from '@mui/icons-material';
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { getOutOfFilterShifts } from "./departmentFilter";

const day_nums = [0, 1, 2, 3, 4, 5, 6];
const date_options = { weekday: "short", month: "short", day: "numeric" };
const today = new Date(new Date().toDateString());
const day_of_week_str = today.toString().substring(0, 3);


function ScheduleDisplay2(props) {
  console.log('ScheduleDisplay2 copilotEnabled:', props.copilotEnabled);

  // EditTeam represents availability
  // Shows the availability display when true, shows the schedule display when false
  const [editTeam, setEditTeam] = React.useState(false);

  // Shows the daily metrics cards at the bottom when true. Per-user
  // preference persisted in localStorage (was a store-level flag).
  const [showDailyMetrics, setShowDailyMetrics] = React.useState(() => {
    const saved = localStorage.getItem("rightwork_showDailyMetrics");
    return saved !== null ? saved === "true" : true;
  });

  React.useEffect(() => {
    localStorage.setItem("rightwork_showDailyMetrics", showDailyMetrics);
  }, [showDailyMetrics]);

  // Toggles between showing daily metrics cards and hiding daily metric cards
  function toggleShowDailyMetrics() {
    setShowDailyMetrics((prevState) => !prevState);
  }

  // Toggles between the schedule display and the availability display.
  //
  // Availability is a plan for a week nobody has worked yet, so the two modes
  // cannot both be on. The effect further down leaves availability when Actual
  // is selected; this is the other direction, which that effect cannot see
  // because showActualHours has not changed. Without it the availability grid
  // comes back under a toolbar still reporting worked hours.
  function toggleEditTeam() {
    const enteringAvailability = !editTeam;
    if (enteringAvailability && props.showActualHours) {
      props.setCheckedMetricsToggle?.(false);
    }
    setEditTeam(enteringAvailability);
  }

  // Owned by HomeDisplay2, which hands the top bar the same answer.
  const checkedMetricsToggle = props.checkedMetricsToggle;


  // Role / department group header band (slimmer than a person row)
  const groupHeaderStyle = {
    height: props.minimizeMode ? "30px" : "36px",
  };

  // Holds the size of the person, shift, and availability boxes
  const box_size_style2 = {
    height: props.minimizeMode ? "38px" : "60px",
    padding: props.minimizeMode ? "3px" : "6px"
  };


  // Labor bar card type (cards vs chart) is owned by HomeDisplay2 so the
  // View menu can set it.
  const bottomCardType = props.bottomCardType || "metric";
  const setBottomCardType = props.setBottomCardType || (() => {});

  const [isFixed, setIsFixed] = React.useState(false);
  // The pinned (scrolled) header is always the compact version.
  const isHeaderCollapsed = isFixed;
  // Compact rows only compact the roster; the header keeps its forecast row and search.
  const headerExpanded = !(isFixed && isHeaderCollapsed);
  const [summaryCardsPlannedExpanded, setSummaryCardsPlannedExpanded] =
    React.useState(false);
  const summaryCardOrder = props.summaryCardOrder || DEFAULT_CARD_ORDER;

  const [notesDrawerState, setNotesDrawerState] = React.useState({
    open: false,
    dateStr: null,
  });

  const [openDropdownDate, setOpenDropdownDate] = React.useState(null);

  const openNotesForDate = (dateStr) => {
    setNotesDrawerState({ open: true, dateStr });
  };

  const closeNotesDrawer = () => {
    setNotesDrawerState({ open: false, dateStr: null });
  };

  const setNotesDrawerOpen = (isOpen, dateStr) => {
    setNotesDrawerState({
      open: isOpen,
      dateStr: isOpen ? dateStr : null,
    });
  };

  // Mirrors WeeklySummaryCards' hasPastData: row 2 only renders when at
  // least one day in the week is past or today.
  const hasPastData =
    !!props.start_date &&
    new Date(props.start_date.toDateString()) <= today;

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      let threshold = 230;
      if (props.showWeeklySummaryCards) {
        // One row of cards is always rendered (forecasted-only, or actual-only
        // when the metrics toggle is on).
        threshold += 110;
        if (checkedMetricsToggle && hasPastData) {
          // Chevron button to expand/collapse the planned row.
          threshold += 32;
          // Second row only appears when the user expands it.
          if (summaryCardsPlannedExpanded) threshold += 110;
        }
      }

      setIsFixed(scrollPosition > threshold);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    props.showWeeklySummaryCards,
    checkedMetricsToggle,
    hasPastData,
    summaryCardsPlannedExpanded,
  ]);

  const header_box_size_style2 = {
    position: "relative",
    height: isFixed && isHeaderCollapsed ? "42px" : "110px",
    padding: "8px"
  }

  // Fixed header style
  const fixed_header_style = {
    position: isFixed ? 'fixed' : 'relative',
    top: isFixed ? '0' : 'auto',
    left: isFixed ? 'calc(60px + var(--ops-dock-width, 0px))' : 'auto',
    right: isFixed ? '0' : 'auto',
    zIndex: isFixed ? '1000' : 'auto',
    backgroundColor: 'white',
    borderBottom: isFixed ? '1px solid #e5e7eb' : 'none',
    boxShadow: isFixed ? '0 2px 4px -2px rgba(0,0,0,0.1)' : 'none',
    paddingLeft: isFixed ? '20px' : '0',
    paddingRight: isFixed ? '20px' : '0'
  };

  // Event handler function for when metrics toggle is clicked in the daily metrics cards
  const handleChangeMetricsToggle = (event) => {
    const checked = event.target.checked;
    props.setCheckedMetricsToggle?.(checked);
    // Punches landing since the week loaded would otherwise stay invisible, and
    // an in-progress shift would sit frozen at its old clamped end time.
    if (checked) props.refreshActualUsedSchedule?.();
  };

  function sum_daily_sales(df) {
    let sales = [];
    day_nums.map((d, i) => {
      const date = props.addDays(start_date, d);
      const date_str = props.formatDate(date);
      const daily_sales = props.agg_column(
        props.filter_df_to_day(df, "date", date_str),
        "value"
      );
      const adjustment = props.filter_df_to_day(
        props.all_data.adjustments_chosen,
        "date",
        date_str
      );
      const adjustment_value =
        adjustment.length === 0 ? 0 : adjustment[0]["adjustment_percent_value"];
      sales.push(daily_sales * adjustment_value + daily_sales);
    });
    return sum(sales);
  }

  function sum_daily_sales_up_to_current_day() {
    // Compare date labels, not weekday positions: each store's week and its
    // overnight cutoff can differ from the browser's calendar.
    const currentBusinessDate = getStoreBusinessDate(props.storeTimezone, props.start_qhnum);
    return sum_daily_sales(
      (props.all_data.forecasted_sales_daily || []).filter(
        (row) => row.date < currentBusinessDate
      )
    );
  }

  function get_date_info(d) {
    const locked = props.lockedDays[d];
    const date = new Date(props.addDays(start_date, d).toDateString());
    const date_str = props.formatDate(date);
    const date_dow = date.getDay() === 0 ? 7 : date.getDay();
    const date_name = date.toLocaleDateString("en-us", date_options);
    const todays_date_yes = props.formatDate(today) === date_str;
    const dow_name = date.toLocaleDateString("en-us", {
      weekday: "long",
    });
    const day_shifts = props.filter_df_to_day(
      props.isCopilotOn
        ? props.all_data.copilot_schedule
        : props.all_data.actual_schedule,
      "date",
      date_str
    );

    const day_shifts_for_length = props.filter_df_to_day(
      props.all_data.actual_schedule, "date", date_str
    )

    // By business date, not clock-in date. The fetch now reaches a day past the
    // week so the final day's overnight punches arrive, and bucketing those by
    // date_start would file them under a day that is not on screen.
    const used_day_shifts = (props.all_data.actual_used_schedule || []).filter(
      (row) => (row?.business_date || row?.date_start) === date_str
    );

    const day_other_store_shifts = props.filter_df_to_day(props.all_data?.actual_schedule, "date", date_str)?.filter((s, i) => s.other_store == true);

    const day_time_off_requests = props.all_data?.all_time_off_requests.filter(
      (request) => {
        // If date_upper_bound is null, then check if date_lower_bound matches the specific date
        if (!request.date_upper_bound) {
          return request.date_lower_bound === date_str;
        }

        // If date_upper_bound is not null, then check if specific date is within the range of date_lower_bound to date_upper_bound
        return (
          request.date_lower_bound <= date_str &&
          request.date_upper_bound >= date_str
        );
      }
    );
    const day_request_off = props.filter_df_to_day(
      props.all_data.request_off,
      "date",
      date_str
    );
    const dow_availability = props.filter_df_to_day(
      props.all_data.default_availability,
      "dow",
      date_dow
    );
    let dow_default_operating_hours = props.filter_df_to_day(
      props.all_data.default_operating_hours,
      "dow",
      date_dow
    );
    dow_default_operating_hours.forEach((v, i) => (v.date = date_str));

    let day_operating_hours = props.filter_df_to_day(
      props.all_data.daily_operating_hours,
      "date",
      date_str
    );
    day_operating_hours.forEach((v, i) => (v.dow = date_dow));

    const edit_mode = locked ? false : date >= today ? true : false;
    const day_in_past = date >= today ? false : true;

    const forecasted_labor_day = props.filter_df_to_day(
      props.all_data.forecasted_labor,
      "date",
      date_str
    );
    const actual_schedule_labor_day = props.filter_df_to_day(
      props.all_data.actual_schedule_labor_deployment,
      "date",
      date_str
    );

    const calculated_earned_labor_day = props.filter_df_to_day(
      props.all_data.calculated_earned_labor,
      "date",
      date_str
    );
    const actual_used_labor_vs_earned_day = props.filter_df_to_day(
      props.all_data.actual_used_labor_vs_earned,
      "date",
      date_str
    );
    const adjustment_chosen = props.filter_df_to_day(
      props.all_data.adjustments_chosen,
      "date",
      date_str
    );

    const adjustments_history = props.filter_df_to_day(
      props.all_data_full?.adjustments_history,
      "date",
      date_str
    );

    const forecast_sales = props.agg_column(
      props.filter_df_to_day(
        props.all_data.forecasted_sales_daily,
        "date",
        date_str
      ),
      "value"
    );

    const actual_sales = props.agg_column(
      props.filter_df_to_day(
        props.all_data.actual_sales_daily,
        "date",
        date_str
      ),
      "value"
    );

    const historical_sales_context = props.filter_df_to_day(
      props.historicalSalesContext,
      "date",
      date_str
    );

    return [
      locked,
      date,
      date_str,
      date_dow,
      date_name,
      todays_date_yes,
      dow_name,
      day_shifts,
      day_request_off,
      dow_availability,
      edit_mode,
      day_in_past,
      forecasted_labor_day,
      actual_schedule_labor_day,
      calculated_earned_labor_day,
      actual_used_labor_vs_earned_day,
      adjustment_chosen,
      forecast_sales,
      actual_sales,
      dow_default_operating_hours,
      day_operating_hours,
      day_time_off_requests,
      historical_sales_context,
      day_other_store_shifts,
      used_day_shifts,
      adjustments_history,
      day_shifts_for_length
    ];
  }

  function get_person_shift_info(
    p,
    date_str,
    date_dow,
    day_shifts,
    day_request_off,
    dow_availability,
    day_time_off_requests,
    day_other_store_shifts
  ) {
    const locked_person = props.lockedPeople[p.id];
    const person_day_shift_all = day_shifts?.filter(
      (s) => s.store_team_member === p.id
    );

    // In actual mode the cards come from the timecard payload, so a published
    // shift appears for the person it was promised to and shows what happened
    // to it, whatever the draft has since done with it.
    const person_day_cards = showActualHours
      ? actualCards.filter(
          (card) =>
            card.store_team_member === p.id && card.date === date_str
        )
      : [];
    const person_day_shift_with_punches = showActualHours
      ? person_day_cards
          .filter((card) => punchRoleInScope(card.role_id, card.role_type))
          // The payload carries a no-show row for every published shift with no
          // timecard, including ones still to come. Drawn as they are, those
          // are the plan: scheduled times on a board that is meant to show what
          // happened. A shift is only missed once its day is over.
          .filter(
            (card) =>
              !card.is_missed ||
              missedShiftHasElapsed(card.date, card.shift_end)
          )
          .sort((a, b) => (a.shift_start ?? 0) - (b.shift_start ?? 0))
      : person_day_shift_all;
    // Work in a department the selector hid still leaves a marker, on the same
    // terms as a card it would have drawn.
    const person_day_unscheduled_hidden = person_day_cards.filter(
      (card) =>
        !punchRoleInScope(card.role_id, card.role_type) &&
        (!card.is_missed || missedShiftHasElapsed(card.date, card.shift_end))
    );

    const person_day_request_off = day_request_off?.filter(
      (s) => s.store_team_member === p.id
    );

    let person_day_time_off_requests = day_time_off_requests?.filter(
      (s) => s.store_team_member === p.id
    );

    let person_day_availability = dow_availability?.filter(
      (s) => s.store_team_member === p.id
    );
    person_day_availability?.forEach(function (element) {
      element.date = date_str;
      element.default = true;
    });
    let person_day_other_store_shift = day_other_store_shifts?.filter(
      (s) => s.user_id === p.user?.id
    );

    return [
      locked_person,
      person_day_shift_with_punches,
      person_day_request_off,
      person_day_availability,
      person_day_time_off_requests,
      person_day_other_store_shift,
      // The unfiltered list. "Outside filter" means a department the selector
      // hid, so it has to be measured against the real schedule: comparing
      // against the actual-mode list would relabel every plan-only shift as
      // filtered when nothing is filtered at all.
      person_day_shift_all,
      person_day_unscheduled_hidden,
    ];
  }

  const average = (array) => array.reduce((a, b) => a + b) / array.length;

  const grade_weights = props.all_data.grade_weights;
  let intraday_weight =
    grade_weights?.length > 0
      ? grade_weights[0]["grade_intraday_weight"]
      : 0.25;
  let budget_weight =
    grade_weights?.length > 0 ? grade_weights[0]["grade_budget_weight"] : 0.75;
  let budget_under_multiplier =
    grade_weights?.length > 0
      ? grade_weights[0]["grade_budget_under_multiplier"]
      : 0.25;
  let budget_over_multiplier =
    grade_weights?.length > 0
      ? grade_weights[0]["grade_budget_over_multiplier"]
      : 1;
  let budget_difficulty_multiplier =
    grade_weights?.length > 0
      ? grade_weights[0]["grade_budget_difficulty_multiplier"]
      : 1;
  let intraday_difficulty_multiplier =
    grade_weights?.length > 0
      ? grade_weights[0]["grade_intraday_difficulty_multiplier"]
      : 1;
  const minimumGradedBestHours = 10;

  function calculate_score(value, best) {
    if (absoluteGradingEnabled(grade_weights?.[0])) {
      return calculateLaborGradeScore({ laborRows: value, targetLaborRows: best, gradeWeights: grade_weights });
    }
    const days = [...new Set(best.map((item) => item.date))];
    let scores = [];

    days.forEach((d) => {
      const best_df = best.filter((r) => r.date === d);
      const value_df = value.filter((r) => r.date === d);

      const best_hours = props.agg_column(best_df, "hours");
      const value_hours = props.agg_column(value_df, "hours");
      if (best_hours < minimumGradedBestHours) {
        return;
      }

      let labor_diff = calculateLaborDifference(value_df, best_df);

      let intraday_cost = 0;
      if (labor_diff["sumLabor"] > 0) {
        intraday_cost =
          (labor_diff["sumDifference"] * intraday_difficulty_multiplier +
            labor_diff["sumLabor"]) /
          labor_diff["sumLabor"];
      }

      // Use local copies to avoid side effects between calls
      let local_intraday_weight = intraday_weight;
      let local_budget_weight = budget_weight;
      const labor_sum = props.agg_column(best_df, "labor");
      if (labor_sum === 0) {
        local_intraday_weight = 0;
        local_budget_weight = 1;
      }

      const score =
        best_hours === 0 || value_hours === 0
          ? 0
          : (intraday_cost * local_intraday_weight +
              (1 -
                (budget_difficulty_multiplier *
                  ((best_hours > value_hours
                    ? budget_under_multiplier
                    : budget_over_multiplier) *
                    Math.abs(best_hours - value_hours))) /
                  best_hours) *
                local_budget_weight) *
            100;
      if (best_hours > 0 && value_hours > 0) {
        scores.push(score);
      }
    });

    if (scores.length > 0) {
      return average(scores);
    } else {
      return null;
    }
  }

  function calculateLaborDifference(value, best) {
    // Aggregate value array by date and qh_num
    const valueAggregated = aggregateArray(value);

    // Aggregate best array by date and qh_num
    const bestAggregated = aggregateArray(best);

    // Join the aggregated arrays by date and qh_num
    const joined = joinData(valueAggregated, bestAggregated);

    // Calculate the sum of the difference between value and best where value is less than best
    let sumDifference = 0;
    for (const item of joined) {
      const difference = item.valueLabor - item.bestLabor;
      if (difference < 0) {
        sumDifference += difference;
      }
    }

    const best_hours = props.agg_column(bestAggregated, "labor");

    return { sumDifference: sumDifference, sumLabor: best_hours };
  }

  function aggregateArray(arr) {
    const aggregated = {};

    for (const item of arr) {
      const key = `${item.date}_${item.qh_num}`;

      if (!aggregated[key]) {
        aggregated[key] = {
          date: item.date,
          qh_num: item.qh_num,
          labor: 0,
        };
      }

      aggregated[key].labor += item.labor;
    }

    return Object.values(aggregated);
  }

  // Helper function to join aggregated data sets with a left join (flipped for value)
  function joinData(value, best) {
    const joinedData = [];
    for (const bestObj of best) {
      const key = bestObj.date + "-" + bestObj.qh_num;
      const valueObj = value.find(
        (obj) => obj.date === bestObj.date && obj.qh_num === bestObj.qh_num
      );
      if (valueObj) {
        joinedData.push({
          date: bestObj.date,
          qh_num: bestObj.qh_num,
          valueLabor: valueObj.labor,
          bestLabor: bestObj.labor,
        });
      } else {
        joinedData.push({
          date: bestObj.date,
          qh_num: bestObj.qh_num,
          valueLabor: 0, // Use 0 as the default value for valueLabor in the flipped left join
          bestLabor: bestObj.labor,
        });
      }
    }
    return joinedData;
  }

  function calculate_daily_budget_score(value, best) {
    if (absoluteGradingEnabled(grade_weights?.[0])) {
      const scores = buildGradeAlignedLaborScores({ laborRows: value, targetLaborRows: best, gradeWeights: grade_weights });
      return Math.min(Math.max(Math.ceil(((scores.budgetScore ?? 0) - 60) / 8), 0), 5);
    }
    const days = [...new Set(best.map((item) => item.date))];
    let scores = [];

    days.forEach((d) => {
      const best_df = best.filter((r) => r.date === d);
      const value_df = value.filter((r) => r.date === d);
      const best_hours = props.agg_column(best_df, "hours");
      const value_hours = props.agg_column(value_df, "hours");
      if (best_hours < minimumGradedBestHours) {
        return;
      }
      const score =
        best_hours === 0 || value_hours === 0
          ? 0
          : (1 -
              ((best_hours > value_hours
                ? budget_under_multiplier
                : budget_over_multiplier) *
                Math.abs(best_hours - value_hours)) /
                best_hours) *
            100;
      if (best_hours > 0 && value_hours > 0) {
        scores.push(score);
      }
    });

    let avg_score = 0;
    if (scores.length > 0) {
      avg_score = average(scores);
    }
    return Math.min(Math.max(Math.ceil((avg_score - 60) / (40 / 5)), 0), 5);
  }

  function calculate_intraday_coverage_score(value, best) {
    if (absoluteGradingEnabled(grade_weights?.[0])) {
      const scores = buildGradeAlignedLaborScores({ laborRows: value, targetLaborRows: best, gradeWeights: grade_weights });
      return Math.min(Math.max(Math.ceil(((scores.coverageScore ?? 0) - 60) / 8), 0), 5);
    }
    const days = [...new Set(best.map((item) => item.date))];
    let scores = [];

    days.forEach((d) => {
      const best_df = best.filter((r) => r.date === d);
      const value_df = value.filter((r) => r.date === d);

      const best_hours = props.agg_column(best_df, "hours");
      const value_hours = props.agg_column(value_df, "hours");
      if (best_hours < minimumGradedBestHours) {
        return;
      }
      let labor_diff = calculateLaborDifference(value_df, best_df);
      let intraday_cost = 0;
      if (labor_diff["sumLabor"] > 0) {
        intraday_cost =
          (labor_diff["sumDifference"] + labor_diff["sumLabor"]) /
          labor_diff["sumLabor"];
      }
      const score =
        best_hours === 0 || value_hours === 0 ? 0 : intraday_cost * 100;
      if (best_hours > 0 && value_hours > 0) {
        scores.push(score);
      }
    });

    let avg_score = 0;
    if (scores.length > 0) {
      avg_score = average(scores);
    }
    return Math.min(Math.max(Math.ceil((avg_score - 60) / (40 / 5)), 0), 5);
  }

  // Function that gives the letter grade based on the score
  function calcLetter(score) {
    if (score === null || score === undefined) {
      return "-";
    }
    if (score >= 90) {
      return "A";
    } else if (score >= 80) {
      return "B";
    } else if (score >= 70) {
      return "C";
    } else if (score >= 60) {
      return "D";
    } else if (score === 0) {
      return "-";
    } else {
      return "F";
    }
  }

  // Function that gives the color of the letter grade based on the score
  function getColor(score) {
    if (score === null || score === undefined) {
      return "gray";
    }
    if (score >= 90) {
      return "#059669";
    } else if (score >= 80) {
      return "#65a30d";
    } else if (score >= 70) {
      return "#f5be27";
    } else if (score >= 60) {
      return "#ea580c";
    } else if (score === 0) {
      return "gray";
    } else {
      return "#dc2626";
    }
  }

  function calculate_letter(value, best) {
    return calcLetter(calculate_score(value, best));
  }

  function calculate_color(value, best) {
    return getColor(calculate_score(value, best));
  }

  function concatenate_column(df, column) {
    let arr = [];
    df.forEach((element) => {
      const new_arr = JSON.parse(element[column].replace(/'/g, '"'));
      let role_type_arr = [];
      if (new_arr.length > 0) {
        new_arr.forEach((i) => {
          role_type_arr.push(element.role_type + " - " + i);
        });
        arr = arr.concat(role_type_arr);
      }
    });
    return arr;
  }

  function combine_data(forecasted_labor_day, actual_schedule_labor_day) {
    // add info to qh
    const qh_labor = [];
    forecasted_labor_day.reduce(function (res, value) {
      if (!res[value.qh_num]) {
        res[value.qh_num] = { qh_num: value.qh_num, labor: 0 };
        qh_labor.push(res[value.qh_num]);
      }
      res[value.qh_num].labor += value.labor;
      return res;
    }, {});

    // Sort qh_labor by qh_num
    qh_labor.sort((a, b) => a.qh_num - b.qh_num);

    const roles = [];
    const data = qh_labor.map((x) => {
      x.actual_labor = props.agg_column(
        actual_schedule_labor_day.filter((q) => q.qh_num === x.qh_num),
        "labor"
      );
      x.role_list_arr = concatenate_column(
        actual_schedule_labor_day.filter((q) => q.qh_num === x.qh_num),
        "role_list"
      );
      x.routine_list_arr = concatenate_column(
        actual_schedule_labor_day.filter((q) => q.qh_num === x.qh_num),
        "routine_list"
      );
      for (const role of x.role_list_arr) {
        x[role] = x[role] ? x[role] + 1 : 1;
        if (!roles.includes(role)) {
          roles.push(role);
        }
      }
      x.time = props.get_t_str(x.qh_num);
      return x;
    });
    return { data: data, roles: roles };
  }

  const open_shifts = props.all_data.actual_schedule
    ? props.all_data.actual_schedule.filter((s) => s.status === "AVAILABLE")
    : [];

  const open_shift_counts = open_shifts.reduce((acc, { date }) => {
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});

  // Get the maximum count
  const maxOpenShiftCount = Math.max(0, ...Object.values(open_shift_counts));

  const eventsData = props.all_data?.events || [];
  const maxDailyNotesCount = day_nums.reduce((max, d) => {
    const dateForCount = props.addDays(
      new Date(props.start_date.toDateString()),
      d
    );
    const dateStrForCount = props.formatDate(dateForCount);
    const dailyEventsForCount = props.filter_df_to_day(
      eventsData,
      "date",
      dateStrForCount
    );
    return Math.max(max, dailyEventsForCount.length);
  }, 0);

  const weekNotesCount = day_nums.reduce((total, d) => {
    const dateForCount = props.addDays(
      new Date(props.start_date.toDateString()),
      d
    );
    const dateStrForCount = props.formatDate(dateForCount);
    return (
      total + props.filter_df_to_day(eventsData, "date", dateStrForCount).length
    );
  }, 0);
  const hasAnyNotes = maxDailyNotesCount > 0;
  const noteCardHeight = 30;
  const noteCardSpacing = 4;
  const noteRowPadding = 12;
  const notesRowHeight = hasAnyNotes
    ? noteCardHeight * maxDailyNotesCount +
      noteCardSpacing * Math.max(maxDailyNotesCount - 1, 0) +
      noteRowPadding
    : 0;

  const notesRowStyle = hasAnyNotes
    ? {
        height: notesRowHeight,
        padding: "6px",
      }
    : null;

  const notesLabelStyle = hasAnyNotes
    ? {
        height: notesRowHeight,
        padding: "6px 12px",
      }
    : null;

  // Shows the drop down for the open shifts if true
  // Open shifts row is always open: tallest day's cards (capped, plus the
  // "+N more" line) and the add cell on editable weeks.
  const openShiftVisibleMax = Math.min(maxOpenShiftCount, OPEN_SHIFT_VISIBLE_CAP);
  const openShiftMoreRows = maxOpenShiftCount > OPEN_SHIFT_VISIBLE_CAP ? 1 : 0;
  const openShiftAddRows = props.week_in_past ? 0 : 1;
  const openShiftRows = Math.max(
    1,
    openShiftVisibleMax + openShiftMoreRows + openShiftAddRows
  );
  const subheader_box_size_style2 = {
    height:
      12 + openShiftRows * (OPEN_SHIFT_ROW_HEIGHT + OPEN_SHIFT_ROW_GAP) - OPEN_SHIFT_ROW_GAP,
    padding: "6px"
  };

  // Row drag-to-reorder only runs on the ungrouped weekly view; grouped views
  // contain synthetic header rows that have no sort_index
  const scheduleReorderActive =
    props.reorderMode &&
    props.weeklyView &&
    !props.store_team_member_weekly_info_view?.some((p) => p.isRoleHeader);

  // Source/destination indexes of an in-progress row drag
  const [rowDrag, setRowDrag] = React.useState(null);

  // Day-column cells live in separate column containers, so they can't follow
  // the cursor during a row drag; instead they snap toward the hovered slot.
  // Row heights are fixed, so each row's displacement is deterministic.
  const getReorderRowShiftStyle = (index) => {
    if (!rowDrag) return null;
    const { sourceIndex, destinationIndex } = rowDrag;
    const rowHeight = props.minimizeMode ? 38 : 60;
    let translate = 0;
    if (index === sourceIndex) {
      translate = (destinationIndex - sourceIndex) * rowHeight;
    } else if (
      sourceIndex < destinationIndex &&
      index > sourceIndex &&
      index <= destinationIndex
    ) {
      translate = -rowHeight;
    } else if (
      destinationIndex < sourceIndex &&
      index >= destinationIndex &&
      index < sourceIndex
    ) {
      translate = rowHeight;
    }
    return {
      transform: `translateY(${translate}px)`,
      transition: "transform 0.2s ease",
      position: "relative",
      zIndex: index === sourceIndex ? 5 : 1,
      ...(index === sourceIndex
        ? { opacity: 0.9, background: "#f5f3ff" }
        : null),
    };
  };

  const renderSearchInput = () => (
    <div className="w-full relative">
      <Search
        className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none"
        style={{ fontSize: "17px" }}
      />
      <input
        className={`w-full h-[30px] pl-8 pr-3 text-xs rounded-lg border outline-none transition-all duration-150 placeholder:text-slate-400 ${
          props.noMatchingSearch
            ? "bg-rose-50 border-rose-300 text-rose-600"
            : "bg-slate-100 border-transparent text-slate-700 hover:bg-slate-200/70 focus:bg-white focus:border-violet-700 focus:shadow-sm"
        }`}
        type="text"
        placeholder="Search by name"
        value={props.searchInput ?? undefined}
        onChange={(event) => props.setSearchInput(event.target.value)}
      />
    </div>
  );

  // Summary + color for each role/department group header row. Walks the
  // roster in order, attributing the people beneath each header to it.
  // Counts shifts from the same schedule the day cells render (Copilot's
  // generated schedule while Copilot is on), so the band matches the rows.
  const groupHeaderShiftSource = props.isCopilotOn
    ? props.all_data?.copilot_schedule
    : props.all_data?.actual_schedule;
  // Per-person actual vs scheduled hours for the metrics toggle. Actuals come
  // from timecards; the scheduled side is trimmed to the same past-or-today
  // window so the pair reads as a like-for-like ratio mid-week instead of
  // comparing hours worked so far against a full week of shifts.
  // The store's business date, not the browser's calendar date: a viewer in
  // another timezone, or a store whose business day starts after midnight,
  // would otherwise roll over early and count a day of shifts nobody has
  // worked yet, understating every ratio until the store caught up.
  const todayStr = props.storeBusinessDate;
  // The store's clock on the schedule's own axis, so a shift that has already
  // ended today counts as elapsed instead of waiting for the business day to
  // roll over.
  const storeHoursNow = getStoreHoursNow(
    props.storeTimezone,
    props.start_qhnum
  );
  // Reading the clock at render is not enough on its own: nothing re-renders
  // when it passes a shift's end, so a shift missed while the board sits open
  // would stay hidden until an unrelated refresh. Shifts end on quarter hours,
  // so that is how often the clock has to be consulted, and holding the
  // quarter hour in state means the memos below see it change. At most four
  // renders an hour, and none at all outside actual mode.
  //
  // props.showActualHours rather than the showActualHours alias below: naming
  // that here would reach it before it is initialised.
  const [storeQuarterHour, setStoreQuarterHour] = React.useState(null);
  React.useEffect(() => {
    if (!props.showActualHours) return undefined;
    const readClock = () =>
      setStoreQuarterHour(
        Math.floor(
          getStoreHoursNow(props.storeTimezone, props.start_qhnum) * 4
        )
      );
    readClock();
    const timer = setInterval(readClock, 60000);
    return () => clearInterval(timer);
  }, [props.showActualHours, props.storeTimezone, props.start_qhnum]);
  // A published shift is only owed once it has been missed, and it has only
  // been missed once its scheduled end has passed. Comparing dates alone would
  // hide a morning absence until tomorrow, leaving it unclassifiable all day
  // and keeping its hours out of the denominator.
  const missedShiftHasElapsed = (date, shiftEnd) =>
    hasElapsed(date, shiftEnd, todayStr, storeHoursNow);
  // Punches are fetched by clock-in date but attributed by business date, and a
  // business date is never later than the date the punch clocked in on. At a
  // store whose day starts after midnight that cuts both ways: an early-morning
  // punch on the week's first calendar day belongs to the previous business
  // week and would pad this week's total, while the final business day is still
  // being worked into the next calendar morning. get_data asks for that trailing
  // day so those punches arrive; both ends are trimmed here.
  const weekStartStr = props.weekStartStr;
  // The fetch reaches a calendar day past the week so the final business day's
  // overnight punches arrive. Those carry the last business date and are kept;
  // an ordinary punch on that eighth day carries the next week's date and is
  // not, which matters most on a completed week, where today is no bound at all.
  const weekEndStr =
    props.start_date && props.addDays
      ? props.formatDate(
          props.addDays(new Date(props.start_date.toDateString()), 6)
        )
      : null;
  const inDisplayedWeekToDate = (date_str) =>
    !!date_str &&
    date_str <= todayStr &&
    (!weekStartStr || date_str >= weekStartStr) &&
    (!weekEndStr || date_str <= weekEndStr);
  // The department selector filters actual_schedule but not actual_used_schedule,
  // which carries no top-level role_type for that filter to match on. Scoping
  // punches here keeps the numerator and denominator measuring the same work:
  // without it, somebody who also worked a hidden department reads as having
  // worked far more than they were scheduled.
  const roleFilterActive =
    props.roleType?.length > 0 &&
    props.roleTypeOptions?.length > 0 &&
    props.roleType.length < props.roleTypeOptions.length;

  // Every card the actual view draws, built from the timecard payload the way
  // the After Action Report builds its rows. That payload already accounts for
  // the day: a row per published shift that produced a timecard, a no-show row
  // per published shift that did not, and a row for anyone who clocked in
  // unscheduled. Reading it instead of the draft schedule means a published
  // shift keeps its card however the draft has since been edited.
  // Role type by role id, so a card can be placed in a department from the role
  // it was worked in. Declared above the memos that call it: they run during
  // render, and a const named before its declaration throws.
  const roleTypeById = React.useMemo(() => {
    const byId = {};
    (props.all_data?.role_names || []).forEach((role) => {
      if (role?.id != null) byId[role.id] = role.role_type;
    });
    return byId;
  }, [props.all_data?.role_names]);

  // Deleting a role nulls role_id on every shift that used it and leaves
  // role_type standing, so a card from a deleted role still knows its
  // department and only the lookup has gone. Scoping by the id alone would
  // drop that work from the department it was done in.
  const punchRoleInScope = (roleId, preservedRoleType) => {
    if (!roleFilterActive) return true;
    const roleType =
      (roleId == null ? null : roleTypeById[roleId]) ?? preservedRoleType;
    return roleType != null && props.roleType.includes(roleType);
  };

  // The cards are what the payload says. The clock still matters to what is
  // drawn from them, which is why the memos below take the quarter hour, but
  // not to the cards themselves.
  const actualCards = React.useMemo(
    () => buildActualCards(props.all_data?.actual_used_schedule),
    [props.all_data?.actual_used_schedule]
  );

  const actualComplianceWarnings = React.useMemo(() =>
    getActualComplianceWarnings(props.laborLawEnabled ? props.actualComplianceAudit?.data : null, actualCards),
    [props.laborLawEnabled, props.actualComplianceAudit?.data, actualCards]
  );

  // What the Day view draws, and so what its axis has to span: a punch outside
  // the selected departments is represented by a compact marker rather than a
  // card, and a shift that has not yet ended has not been missed.
  const visibleActualCards = React.useMemo(
    () =>
      actualCards.filter(
        (card) =>
          punchRoleInScope(card.role_id, card.role_type) &&
          (!card.is_missed ||
            missedShiftHasElapsed(card.date, card.shift_end))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actualCards,
      todayStr,
      storeQuarterHour,
      roleFilterActive,
      props.roleType,
      props.all_data?.role_names,
    ]
  );

  const hoursToDate = React.useMemo(() => {
    const actual = {};
    const scheduled = {};
    const actualByDate = {};
    const scheduledByDate = {};
    const countedPublishedIds = new Set();
    const publishedCoverageShifts = [];

    actualCards.forEach((card) => {
      const memberId = card.store_team_member;
      if (memberId == null || !inDisplayedWeekToDate(card.date)) return;

      // The two halves of the ratio are filtered by their own department. A
      // shift published in one and clocked into another belongs to each side
      // under a different selection, so asking the punch's role about the plan
      // would set one department's work against another's scheduling, and
      // selecting the scheduled department would drop the shift from both.
      if (punchRoleInScope(card.role_id, card.role_type)) {
        const worked = card.hours || 0;
        actual[memberId] = (actual[memberId] || 0) + worked;
        if (!actualByDate[memberId]) actualByDate[memberId] = {};
        actualByDate[memberId][card.date] =
          (actualByDate[memberId][card.date] || 0) + worked;
      }

      // The denominator counts published shifts, not cards. One punch can
      // cover two shifts and one shift can produce two punch rows, so the
      // shifts a card covers are counted by id and a shift already counted is
      // not owed twice.
      // A punch that clocked no role and covers two departments belongs to
      // neither, so its worked hours are left out of both. Its shifts have to
      // go with them: counting the plan while the work it answers for is
      // missing reads as nothing worked against hours owed, which is a worse
      // account of the day than leaving the punch out of a filtered view
      // altogether. Unfiltered, the card and its hours are all still there.
      const cardDepartmentUnknown =
        card.role_id == null && card.role_type == null;

      (card.covered_published || []).forEach((entry) => {
        if (entry?.id == null || countedPublishedIds.has(entry.id)) return;
        countedPublishedIds.add(entry.id);
        if (cardDepartmentUnknown && roleFilterActive) return;
        if (!punchRoleInScope(entry.role_id, entry.role_type)) return;

        // A shift the manager called off was never owed, and an unscheduled
        // punch was never promised, so neither carries scheduled hours. A
        // shift whose end has not yet passed has not been missed, so it is
        // not part of what the day owed either.
        if (isManagerCancelledShift(entry)) return;
        if (
          card.is_missed &&
          !missedShiftHasElapsed(card.date, entry.shift_end)
        ) {
          return;
        }

        // Owed to whoever was scheduled, not to whoever covered it. When a
        // coworker picks up someone's shift the assignee has no card of their
        // own, so charging the coworker would read as their commitment and
        // leave the assignee at worked hours over nothing.
        const owedTo = entry.store_team_member ?? memberId;
        publishedCoverageShifts.push({ ...entry, date: card.date });
        const promised = entry.hours || 0;
        scheduled[owedTo] = (scheduled[owedTo] || 0) + promised;
        if (!scheduledByDate[owedTo]) scheduledByDate[owedTo] = {};
        scheduledByDate[owedTo][card.date] =
          (scheduledByDate[owedTo][card.date] || 0) + promised;
      });
    });

    return { actual, scheduled, actualByDate, scheduledByDate, publishedCoverageShifts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actualCards,
    todayStr,
    storeQuarterHour,
    weekStartStr,
    weekEndStr,
    roleFilterActive,
    props.roleType,
    props.all_data?.role_names,
  ]);

  // The actual/scheduled pair only means something once some of the week has
  // happened; before that the roster keeps showing plain scheduled hours.
  // Measured in store time, like the cutoff itself: hasPastData is browser
  // local and mirrors WeeklySummaryCards for the scroll threshold, so reusing
  // it here would turn on the pair while the store is still on last week and
  // print 0.0 / 0.0 for everybody.
  const showActualHours = props.showActualHours;


  // Availability is a plan for a week nobody has worked yet, so it cannot be
  // shown in actual mode. Selecting Actual from the availability grid means
  // the manager wants what happened, and leaving editTeam set would keep the
  // availability cells under an actual toolbar reporting worked hours.
  React.useEffect(() => {
    if (showActualHours) setEditTeam(false);
  }, [showActualHours]);

  // Availability mode has no use for open shifts, and neither does actual mode:
  // an open shift is a plan nobody was on. Both halves of the row read this one
  // value, because the label and the day cells are separate elements and a row
  // that vanishes from only one of them knocks the grid out of step with the
  // roster beside it.
  const showOpenShiftsRow = !editTeam;
  // The row stays in actual mode so the grid keeps its shape, but an open shift
  // is a plan nobody was on, so it reads as inactive and cannot be clicked.
  const openShiftsRowMuted = showActualHours
    ? "opacity-40 pointer-events-none"
    : "";

  // Worked times keyed by the published shift they cover, so a card can show
  // what happened in place of what was planned.
  const actualTimesByPublishedId = React.useMemo(
    () => buildActualTimesByPublishedId(props.all_data?.actual_used_schedule),
    [props.all_data?.actual_used_schedule]
  );

  // Which shifts a timecard vouches for, multi-shift punches included, so a
  // card is not called unworked on the strength of a flag that predates the
  // punch refresh.
  const coveredPublishedIds = React.useMemo(
    () => buildCoveredPublishedIds(props.all_data?.actual_used_schedule),
    [props.all_data?.actual_used_schedule]
  );

  // Who actually clocked each shift, so a coworker's punch is not drawn on the
  // card of the person who was scheduled.
  const coverageOwners = React.useMemo(
    () => buildCoverageOwners(props.all_data?.actual_used_schedule),
    [props.all_data?.actual_used_schedule]
  );

  // Punches with nothing scheduled behind them. The hours already count these,
  const groupHeaderInfo = React.useMemo(() => {
    const rows = props.store_team_member_weekly_info_view || [];
    const shifts = (groupHeaderShiftSource || []).filter(
      (sh) => sh.store_team_member !== null && sh.other_store === false
    );
    const shiftCountByMember = {};
    shifts.forEach((sh) => {
      shiftCountByMember[sh.store_team_member] =
        (shiftCountByMember[sh.store_team_member] || 0) + 1;
    });
    const info = {};
    let current = null;
    rows.forEach((row) => {
      if (row.isRoleHeader) {
        current = {
          people: 0,
          shifts: 0,
          hours: 0,
          actualHours: 0,
          scheduledHoursToDate: 0,
        };
        info[row.id] = current;
        return;
      }
      if (!current) return;
      current.people += 1;
      current.shifts += shiftCountByMember[row.id] || 0;
      current.hours += row.hours || 0;
      current.actualHours += hoursToDate.actual[row.id] || 0;
      current.scheduledHoursToDate += hoursToDate.scheduled[row.id] || 0;
    });
    return info;
  }, [props.store_team_member_weekly_info_view, groupHeaderShiftSource, hoursToDate]);

  const renderGroupHeaderLabel = (p) => {
    const info = groupHeaderInfo[p.id];
    const roleColorClass = props.all_data?.role_names?.find(
      (r) => r.role_name === p.roleName
    )?.color;
    return (
      <div className="flex items-center gap-2.5 px-3 min-w-0">
        {roleColorClass && (
          <span className={`w-2.5 h-2.5 rounded-[3px] shrink-0 ${roleColorClass}`} />
        )}
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider whitespace-nowrap">
          {p.roleName}
        </h3>
        {info && (
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {info.people} {info.people === 1 ? "person" : "people"} · {info.shifts}{" "}
            {info.shifts === 1 ? "shift" : "shifts"} ·{" "}
            {showActualHours
              ? `${info.actualHours.toFixed(2)} / ${info.scheduledHoursToDate.toFixed(2)} h`
              : `${info.hours.toFixed(2)} h`}
          </span>
        )}
      </div>
    );
  };

  const renderPersonCard = (p) => (
    <PersonCard
      key={p.id}
      p={p}
      openDirectMessageWith={props.openDirectMessageWith}
      currentUserId={props.currentUserId}
      openEmployeeProfile={props.openEmployeeProfile}
      role_names={props.all_data.role_names}
      updatePersonInfo={props.updatePersonInfo}
      lock_person={props.lock_person}
      locked_person={props.lockedPeople[p.id]}
      id={p.id}
      week_in_past={props.week_in_past}
      week_already_started={props.week_already_started}
      minimizeMode={props.minimizeMode}
      role_types={props.all_data.role_types}
      loadEmployeePreviousSchedule={props.loadEmployeePreviousSchedule}
      proficiencyStarsCount={props.proficiencyStarsCount}
      proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
      actualEmployeeWarnings={actualComplianceWarnings.byEmployee[p.id] || []}
      laborLawViolations={props.laborLawViolations}
      laborLawEnabled={props.laborLawEnabled}
      showActualHours={showActualHours}
      actualHours={hoursToDate.actual[p.id] || 0}
      scheduledHoursToDate={hoursToDate.scheduled[p.id] || 0}
    />
  );

  // Team members associated with store
  if (props.store_team_member_weekly_info_view.length > 0 && !props.loading) {
    var start_date = new Date(props.start_date.toDateString());
    {
      return props.monthlyView ? (
        <MonthlySchedule {...props} />
      ) : props.weeklyView ? (
        <div>
          {props.showWeeklySummaryCards && (
            <WeeklySummaryCards
              actualComplianceAudit={props.actualComplianceAudit}
              all_data={props.all_data}
              start_date={start_date}
              addDays={props.addDays}
              formatDate={props.formatDate}
              agg_column={props.agg_column}
              filter_df_to_day={props.filter_df_to_day}
              sum_daily_sales={sum_daily_sales}
              open_shifts={open_shifts}
              countOfWarnings={props.countOfWarnings}
              laborLawEnabled={props.laborLawEnabled}
              laborLawViolations={props.laborLawViolations}
              checkedMetricsToggle={checkedMetricsToggle}
              store_id={props.store_id}
              plannedExpanded={summaryCardsPlannedExpanded}
              setPlannedExpanded={setSummaryCardsPlannedExpanded}
              cardOrder={summaryCardOrder}
            />
          )}

          {/* Fixed header container */}
          <div style={fixed_header_style} className="flex">
            {/* Column 1 - Left sidebar header */}
            <div className={`${props.windowWidth <= 1279 ? "w-[180px]" : "w-[200px]"} min-w-[160px]`}>
              <div style={header_box_size_style2} className="w-full">
                <div className="w-full">
                  <EditTeamButton2
                    toggleEditTeam={toggleEditTeam}
                    editTeam={editTeam}
                    minimizeMode={props.minimizeMode}
                    windowWidth={props.windowWidth}
                    isFixed={isFixed}
                    isHeaderCollapsed={isHeaderCollapsed}
                  />
                </div>
                {headerExpanded && (
                  <div className="w-full mt-2">
                    {renderSearchInput()}
                  </div>
                )}
              </div>
            </div>

            {/* Columns 2-8 - Date headers */}
            {day_nums.map((d, i) => {
              const [
                locked,
                date,
                date_str,
                date_dow,
                date_name,
                todays_date_yes,
                dow_name,
                day_shifts,
                day_request_off,
                dow_availability,
                edit_mode,
                day_in_past,
                forecasted_labor_day,
                actual_schedule_labor_day,
                calculated_earned_labor_day,
                actual_used_labor_vs_earned_day,
                adjustment_chosen,
                forecast_sales,
                actual_sales,
                dow_default_operating_hours,
                day_operating_hours,
                day_time_off_requests,
                historical_sales_context,
                day_other_store_shifts,
                used_day_shifts,
                adjustments_history,
                day_shifts_for_length
              ] = get_date_info(d);

              const daily_weather = props.filter_df_to_day(
                props.all_data.weather,
                "date",
                date_str
              );

              const daily_local_events = props.filter_df_to_day(
                props.all_data.local_events,
                "date",
                date_str
              );

              const daily_forecast_exclude_date = props.filter_df_to_day(
                props.all_data.forecast_exclude_dates,
                "date",
                date_str
              );

              const daily_blackout_date = props.filter_df_to_day(
                props.blackout_dates,
                "date",
                date_str
              );

              const daily_forecast_other = props.filter_df_to_day(
                props.all_data.forecasted_other_daily,
                "date",
                date_str
              );

              const daily_actual_other = props.filter_df_to_day(
                props.all_data.actual_other_daily,
                "date",
                date_str
              );

              const daily_labor_percent_data = props.filter_df_to_day(
                props.laborPercentData,
                "date",
                date_str
              );

              const dayOfWeekStr = date.toLocaleDateString("en-us", {
                weekday: "short",
              });

              const daily_batch_guidance =
                date_str in props.all_data.batch_guidance
                  ? props.all_data.batch_guidance[date_str]
                  : {};

              const daily_intraday_metrics =
                date_str in props.all_data.intraday_metrics
                  ? props.all_data.intraday_metrics[date_str]
                  : {};

              const actual_intraday_metrics =
                date_str in props.all_data.actual_intraday_metrics
                  ? props.all_data.actual_intraday_metrics[date_str]
                  : {};

              const daily_reservation_summary =
                props.all_data.reservation_summary?.find(
                  (r) => r.date === date_str
                ) || null;

              const daily_adjustment =
                (adjustment_chosen.length === 0
                  ? 0
                  : adjustment_chosen[0]["adjustment_percent_value"]) + 1;

              const daily_forecast_transactions =
                props.agg_column(
                  props.filter_df_to_day(
                    props.all_data.forecasted_transactions_daily,
                    "date",
                    date_str
                  ),
                  "value"
                ) * daily_adjustment;

              const daily_forecast_sales = daily_adjustment * forecast_sales;

              const daily_open_shifts = props.filter_df_to_day(
                open_shifts,
                "date",
                date_str
              );

              const daily_events = props.filter_df_to_day(
                props.all_data.events,
                "date",
                date_str
              );

              const day_num_employees_working = new Set(
                day_shifts_for_length?.filter(shift =>
                  shift.store_team_member !== null && shift.other_store === false
                )?.map(shift => shift.store_team_member)
              )?.size;

              return (
                <div key={i} className="flex-1 min-w-[100px] border-l border-gray-200">
                  <div style={header_box_size_style2} className="bg-white">
                    <DateCard2
                      date_name={date.toLocaleDateString("en-us", date_options)}
                      day_of_week_str={day_of_week_str}
                      forecast_sales={forecast_sales}
                      actual_sales={actual_sales}
                      lock_day={props.lock_day}
                      locked={locked}
                      d={d}
                      i={i}
                      edit_mode={edit_mode}
                      day_in_past={day_in_past}
                      todays_date_yes={todays_date_yes}
                      adjustment_chosen={adjustment_chosen}
                      addForecastAdjustment={props.addForecastAdjustment}
                      addManualActualSales={props.addManualActualSales}
                      addAdjustmentFinished={props.addAdjustmentFinished}
                      date_str={date_str}
                      toggleDailyWeekly={props.toggleDailyWeekly}
                      daily_weather={daily_weather}
                      daily_events={daily_events}
                      daily_local_events={daily_local_events}
                      saveEvent={props.saveEvent}
                      updateEvent={props.updateEvent}
                      deleteEvent={props.deleteEvent}
                      eventAttachmentProps={props.eventAttachmentProps}
                      saveEventFinished={props.saveEventFinished}
                      setSaveEventFinished={props.setSaveEventFinished}
                      saveForecastExcludeDate={props.saveForecastExcludeDate}
                      deleteForecastExcludeDate={
                        props.deleteForecastExcludeDate
                      }
                      daily_forecast_exclude_date={daily_forecast_exclude_date}
                      saveBlackoutDate={props.saveBlackoutDate}
                      deleteBlackoutDate={props.deleteBlackoutDate}
                      daily_blackout_date={daily_blackout_date}
                      openDropdownDate={openDropdownDate}
                      setOpenDropdownDate={setOpenDropdownDate}
                      dailyTemplateProps={props.dailyTemplateProps}
                      isFixed={isFixed}
                      historical_sales_context={historical_sales_context}
                      get_date_info={get_date_info}
                      edit_hoo={props.edit_hoo}
                      get_t_str={get_t_str}
                      start_qhnum={props.start_qhnum}
                      schedule_day_end_qhnum={props.schedule_day_end_qhnum}
                      minimizeMode={false}
                      isHeaderCollapsed={isHeaderCollapsed}
                      windowWidth={props.windowWidth}
                      daily_intraday_metrics={daily_intraday_metrics}
                      actual_intraday_metrics={actual_intraday_metrics}
                      daily_reservation_summary={daily_reservation_summary}
                      reservation_summary={props.all_data.reservation_summary}
                      adjustments_history={adjustments_history}
                      day_num_employees_working={day_num_employees_working}
                      notesDrawerState={notesDrawerState}
                      openNotesForDate={openNotesForDate}
                      closeNotesDrawer={closeNotesDrawer}
                      setNotesDrawerOpen={setNotesDrawerOpen}
                      manualSalesInputEnabled={props.manualSalesInputEnabled}
                      fetchManualIntradayDistribution={
                        props.fetchManualIntradayDistribution
                      }
                      saveManualIntradayDistribution={
                        props.saveManualIntradayDistribution
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Spacer when header is fixed to prevent content jump */}
          {isFixed && (
            <div style={{ height: "110px" }} />
          )}

          {/* Rest of content - search bar, employee rows, etc. */}
          <div className="w-full flex">
            {/* Column 1 - Left sidebar content */}
            <div className={`${props.windowWidth <= 1279 ? "w-[180px]" : "w-[200px]"} min-w-[160px]`}>
              {hasAnyNotes && (
                <div
                  style={notesLabelStyle}
                  className="w-full border-t border-gray-200 flex items-center bg-white px-3"
                >
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                    <span>Notes</span>
                    <span
                      className={`ml-0.5 rounded-full px-1.5 py-px text-xxs font-bold ${
                        weekNotesCount > 0
                          ? "bg-violet-100 text-violet-700"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {weekNotesCount}
                    </span>
                  </div>
                </div>
              )}
              {/* Open shifts label (hidden in availability mode) */}
              {showOpenShiftsRow && (
              <div
                style={subheader_box_size_style2}
                className={`w-full border-t border-gray-200 ${openShiftsRowMuted}`}
              >
                {headerExpanded ? (
                  <div className="w-full h-[30px] flex items-center gap-1.5 px-1 text-xs font-semibold text-slate-600">
                    <span>Open shifts</span>
                    <span
                      className={`ml-0.5 rounded-full px-1.5 py-px text-xxs font-bold ${
                        open_shifts.length > 0
                          ? "bg-violet-100 text-violet-700"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {open_shifts.length}
                    </span>
                  </div>
                ) : null}
              </div>
              )}

              {/* Employee list */}
              {scheduleReorderActive ? (
                <DragDropContext
                  onDragStart={(start) =>
                    setRowDrag({
                      sourceIndex: start.source.index,
                      destinationIndex: start.source.index,
                    })
                  }
                  onDragUpdate={(update) =>
                    setRowDrag(
                      (prev) =>
                        prev && {
                          ...prev,
                          destinationIndex: update.destination
                            ? update.destination.index
                            : prev.sourceIndex,
                        }
                    )
                  }
                  onDragEnd={(result) => {
                    setRowDrag(null);
                    if (!result.destination) return;
                    props.reorderTeamMembers(
                      result.source.index,
                      result.destination.index
                    );
                  }}
                >
                  <Droppable droppableId="schedule-employee-order">
                    {(droppableProvided) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                      >
                        {props.store_team_member_weekly_info_view?.map(
                          (p, index) => (
                            <Draggable
                              key={p.id}
                              draggableId={String(p.id)}
                              index={index}
                            >
                              {(dragProvided, dragSnapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  className={`w-full border-t border-gray-200 bg-white flex items-center cursor-grab ${
                                    dragSnapshot.isDragging
                                      ? "shadow-lg ring-1 ring-violet-300"
                                      : ""
                                  }`}
                                  style={{
                                    ...box_size_style2,
                                    ...dragProvided.draggableProps.style,
                                  }}
                                >
                                  <DragIndicatorIcon
                                    sx={{
                                      fontSize: 18,
                                      color: "#94a3b8",
                                      flexShrink: 0,
                                      ml: "-4px",
                                      mr: "4px",
                                    }}
                                  />
                                  <div className="flex-1 min-w-0 h-full">
                                    {renderPersonCard(p)}
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          )
                        )}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                props.store_team_member_weekly_info_view?.map((p, i) => {
                  // Check if this is a role header
                  if (p.isRoleHeader) {
                    return (
                      <div
                        key={p.id}
                        className="w-full border-t border-slate-200 bg-slate-50 flex items-center relative z-10"
                        style={groupHeaderStyle}
                      >
                        {renderGroupHeaderLabel(p)}
                      </div>
                    );
                  }

                  // Regular employee row
                  return (
                    <div
                      className="w-full border-t border-gray-200"
                      style={box_size_style2}
                      key={p.id}
                    >
                      {renderPersonCard(p)}
                    </div>
                  );
                })
              )}
            </div>

            {/* Columns 2-8 - Daily content */}
            {day_nums.map((d, i) => {
              const [
                locked,
                date,
                date_str,
                date_dow,
                date_name,
                todays_date_yes,
                dow_name,
                day_shifts,
                day_request_off,
                dow_availability,
                edit_mode,
                day_in_past,
                forecasted_labor_day,
                actual_schedule_labor_day,
                calculated_earned_labor_day,
                actual_used_labor_vs_earned_day,
                adjustment_chosen,
                forecast_sales,
                actual_sales,
                dow_default_operating_hours,
                day_operating_hours,
                day_time_off_requests,
                historical_sales_context,
                day_other_store_shifts,
                used_day_shifts,
                adjustments_history,
                day_shifts_for_length
              ] = get_date_info(d);

              const daily_weather = props.filter_df_to_day(
                props.all_data.weather,
                "date",
                date_str
              );
              const daily_local_events = props.filter_df_to_day(
                props.all_data.local_events,
                "date",
                date_str
              );

              const daily_forecast_exclude_date = props.filter_df_to_day(
                props.all_data.forecast_exclude_dates,
                "date",
                date_str
              );

              const daily_forecast_other = props.filter_df_to_day(
                props.all_data.forecasted_other_daily,
                "date",
                date_str
              );

              const daily_actual_other = props.filter_df_to_day(
                props.all_data.actual_other_daily,
                "date",
                date_str
              );

              const unfilteredDayShifts = props.isCopilotOn
                ? day_shifts
                : props.filter_df_to_day(
                    props.all_data_full?.actual_schedule || [],
                    "date",
                    date_str
                  );

              const daily_labor_percent_data = props.filter_df_to_day(
                props.laborPercentData,
                "date",
                date_str
              );

              const dayOfWeekStr = date.toLocaleDateString("en-us", {
                weekday: "short",
              });

              const daily_batch_guidance =
                date_str in props.all_data.batch_guidance
                  ? props.all_data.batch_guidance[date_str]
                  : {};

              const daily_intraday_metrics =
                date_str in props.all_data.intraday_metrics
                  ? props.all_data.intraday_metrics[date_str]
                  : {};

              const actual_intraday_metrics =
                date_str in props.all_data.actual_intraday_metrics
                  ? props.all_data.actual_intraday_metrics[date_str]
                  : {};

              const daily_reservation_summary =
                props.all_data.reservation_summary?.find(
                  (r) => r.date === date_str
                ) || null;

              const daily_adjustment =
                (adjustment_chosen.length === 0
                  ? 0
                  : adjustment_chosen[0]["adjustment_percent_value"]) + 1;

              const daily_forecast_transactions =
                props.agg_column(
                  props.filter_df_to_day(
                    props.all_data.forecasted_transactions_daily,
                    "date",
                    date_str
                  ),
                  "value"
                ) * daily_adjustment;

              const daily_forecast_sales = daily_adjustment * forecast_sales;

              const daily_open_shifts = props.filter_df_to_day(
                open_shifts,
                "date",
                date_str
              );

              const daily_events = props.filter_df_to_day(
                props.all_data.events,
                "date",
                date_str
              );

              const day_num_employees_working = new Set(
                day_shifts_for_length?.filter(shift =>
                  shift.store_team_member !== null && shift.other_store === false
                )?.map(shift => shift.store_team_member)
              )?.size;

              return (
                <div key={i} className="flex-1 min-w-[100px] border-l border-gray-200">
                  {hasAnyNotes && (
                    <EventsNotesRow
                      dailyEvents={daily_events}
                      boxStyle={notesRowStyle}
                      onOpenNotes={() => openNotesForDate(date_str)}
                    />
                  )}

                  {/* Open shifts dropdown (hidden in availability mode) */}
                  {showOpenShiftsRow ? (
                  <div className={openShiftsRowMuted}>
                  <OpenShiftsDropdown
                    showActualTimes={props.showActualHours}
                    laborLawViolations={props.laborLawViolations}
                    paidBreakInputsEnabled={props.paidBreakInputsEnabled}
                    restBreakRegulation={props.restBreakRegulation}
                    daily_open_shifts={daily_open_shifts}
                    edit_mode={edit_mode}
                    subheader_box_size_style2={subheader_box_size_style2}
                    all_data={props.all_data}
                    week_in_past={props.week_in_past}
                    date_str={date_str}
                    addShift={props.addShift}
                    store_id={props.store_id}
                    get_t_str={props.get_t_str}
                    date_name={date_name}
                    saveShiftFinished={props.saveShiftFinished}
                    setSaveShiftFinished={props.setSaveShiftFinished}
                    d={d}
                    copiedShift={props.copiedShift}
                    setCopiedShift={props.setCopiedShift}
                    addMultipleShifts={props.addMultipleShifts}
                    minimizeMode={props.minimizeMode}
                    businessWeekStartDayIndex={props.businessWeekStartDayIndex}
                    start_qhnum={props.start_qhnum}
                    schedule_day_end_qhnum={props.schedule_day_end_qhnum}
                    defaultBreakLengthHours={props.defaultBreakLengthHours}
                    breakRequiredWhenShiftLengthExceeds={props.breakRequiredWhenShiftLengthExceeds}
                    secondBreakShiftLengthThreshold={props.secondBreakShiftLengthThreshold}
                    businessDeclineShiftOptionEnabled={
                      props.businessDeclineShiftOptionEnabled
                    }
                    store_team_member_weekly_info={
                      props.store_team_member_weekly_info
                    }
                    lockAllShifts={props.lockAllShifts}
                    p={props.p}
                    windowWidth={props.windowWidth}
                  />
                  </div>
                  ) : null}

                  {/* Employee shifts/availability */}
                  {props.store_team_member_weekly_info_view.map((p, i) => {
                    // Skip rendering shift content for role headers
                    if (p.isRoleHeader) {
                      return (
                        <div
                          key={p.id}
                          className="border-t border-slate-200 bg-slate-50 relative -ml-px"
                          style={{ ...groupHeaderStyle, width: "calc(100% + 1px)" }}
                        >
                        </div>
                      );
                    }

                    const [
                      locked_person,
                      person_day_shift,
                      person_day_request_off,
                      person_day_availability,
                      person_day_time_off_requests,
                      person_day_other_store_shift,
                      person_day_shift_all,
                      person_day_unscheduled_hidden,
                    ] = get_person_shift_info(
                      p,
                      date_str,
                      date_dow,
                      day_shifts,
                      day_request_off,
                      dow_availability,
                      day_time_off_requests,
                      day_other_store_shifts
                    );
                    // In actual mode a marker stands for work the department
                    // filter hid, so it is built from the punches. Walking the
                    // draft as well would also mark the plan an employee
                    // departed from: a shift scheduled in a hidden department
                    // that they actually clocked into a visible one draws its
                    // worked card, and the draft would add a marker
                    // contradicting it for the same hour of work.
                    const personDayOutOfFilterDisplay = showActualHours
                      ? person_day_unscheduled_hidden || []
                      : getOutOfFilterShifts(
                          p,
                          [
                            ...(person_day_shift_all || []),
                            ...(person_day_other_store_shift || []),
                          ],
                          unfilteredDayShifts
                        ).concat(person_day_unscheduled_hidden || []);
                    return (
                      <div
                        className="flex justify-center items-center border-t border-gray-200"
                        style={{
                          ...box_size_style2,
                          ...(scheduleReorderActive
                            ? getReorderRowShiftStyle(i)
                            : null),
                        }}
                        key={p.id}
                      >
                        {editTeam ? (
                          <PersonDayAvailabilityDisplay
                            edit_mode={edit_mode}
                            displayMode={props.displayMode}
                            minimizeMode={props.minimizeMode}
                            date_name={date_name}
                            get_t_str={props.get_t_str}
                            dow_name={dow_name}
                            date_dow={date_dow}
                            date_str={date_str}
                            p={p}
                            editAvailability={props.editAvailability}
                            person_day_availability={person_day_availability}
                            person_day_request_off={person_day_request_off}
                            saveAvailabilityFinished={
                              props.saveAvailabilityFinished
                            }
                            setSaveAvailabilityFinished={
                              props.setSaveAvailabilityFinished
                            }
                            d={d}
                            person_day_time_off_requests={
                              person_day_time_off_requests
                            }
                            businessWeekStartDayIndex={
                              props.businessWeekStartDayIndex
                            }
                            start_qhnum={props.start_qhnum}
                            schedule_day_end_qhnum={props.schedule_day_end_qhnum}
                            request_time_off={props.request_time_off}
                            delete_time_off_request={
                              props.delete_time_off_request
                            }
                            delete_temporary_availability={
                              props.delete_temporary_availability
                            }
                            windowWidth={props.windowWidth}
                          />
                        ) : (
                          <PersonDayShiftDisplay
                            edit_mode={edit_mode}
                            lockAllShifts={props.lockAllShifts}
                            locked_person={locked_person}
                            displayMode={props.displayMode}
                            minimizeMode={props.minimizeMode}
                            date_name={date_name}
                            get_t_str={props.get_t_str}
                            person_day_shift={person_day_shift}
                            person_day_out_of_filter_shifts={
                              personDayOutOfFilterDisplay
                            }
                            p={p}
                            date_str={date_str}
                            addShift={props.addShift}
                            store_id={props.store_id}
                            person_day_availability={person_day_availability}
                            role_names={props.all_data.role_names}
                            saveShiftFinished={props.saveShiftFinished}
                            setSaveShiftFinished={props.setSaveShiftFinished}
                            d={d}
                            setCopiedShift={props.setCopiedShift}
                            copiedShift={props.copiedShift}
                            addMultipleShifts={props.addMultipleShifts}
                            person_day_time_off_requests={
                              person_day_time_off_requests
                            }
                            person_day_other_store_shift={
                              person_day_other_store_shift
                            }
                            role_types={props.all_data.role_types}
                            businessWeekStartDayIndex={
                              props.businessWeekStartDayIndex
                            }
                            start_qhnum={props.start_qhnum}
                            schedule_day_end_qhnum={props.schedule_day_end_qhnum}
                            storeTimezone={props.storeTimezone}
                            frequentShifts={props.frequentShifts}
                            day_in_past={day_in_past}
                            updatePersonInfo={props.updatePersonInfo}
                            post_missed_shift={props.post_missed_shift}
                            breakWaiverEnabled={props.breakWaiverEnabled}
                            showUnpublishedShifts={props.showUnpublishedShifts}
                            defaultBreakLengthHours={
                              props.defaultBreakLengthHours
                            }
                            editShiftsInPastEnabled={
                              props.editShiftsInPastEnabled
                            }
                            isCopilotOn={props.isCopilotOn}
                            actualWarningsByEntry={actualComplianceWarnings.byEntry}
                            showActualTimes={showActualHours}
                            actualTimesByPublishedId={actualTimesByPublishedId}
                            coveredPublishedIds={coveredPublishedIds}
                            coverageOwners={coverageOwners}
                            breakRequiredWhenShiftLengthExceeds={
                              props.breakRequiredWhenShiftLengthExceeds
                            }
                            secondBreakShiftLengthThreshold={
                              props.secondBreakShiftLengthThreshold
                            }
                            paidBreakInputsEnabled={
                              props.paidBreakInputsEnabled
                            }
                            businessDeclineShiftOptionEnabled={
                              props.businessDeclineShiftOptionEnabled
                            }
                            restBreakRegulation={
                              props.restBreakRegulation
                            }
                            windowWidth={props.windowWidth}
                            laborLawViolations={props.laborLawViolations}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Fixed Bottom Metrics Bar */}
          <FixedBottomMetricsBar
            openLiveAction={props.openLiveAction}
            actualCards={visibleActualCards}
            publishedScheduledHours={Object.values(hoursToDate.scheduled).reduce((sum, hours) => sum + hours, 0)}
            scheduledHoursByMemberDate={hoursToDate.scheduledByDate}
          publishedCoverageShifts={hoursToDate.publishedCoverageShifts}
            actualHoursAvailable={props.actualHoursAvailable}
            showDailyMetrics={showDailyMetrics}
            toggleShowDailyMetrics={toggleShowDailyMetrics}
            bottomCardType={bottomCardType}
            setBottomCardType={setBottomCardType}
            checkedMetricsToggle={checkedMetricsToggle}
            handleChangeMetricsToggle={handleChangeMetricsToggle}
            weekHasStartedForActuals={props.weekHasStartedForActuals}
            props={props}
            get_date_info={get_date_info}
            calculate_letter={calculate_letter}
            calculate_color={calculate_color}
            calculate_daily_budget_score={calculate_daily_budget_score}
            calculate_intraday_coverage_score={calculate_intraday_coverage_score}
            combine_data={combine_data}
            sum_daily_sales={sum_daily_sales}
            sum_daily_sales_up_to_current_day={sum_daily_sales_up_to_current_day}
            open_shifts={open_shifts}
            today={today}
            start_date={start_date}
            refreshActualsForDate={props.refreshActualsForDate}
            pos_provider={props.pos_provider}
            windowWidth={props.windowWidth}
          />
        </div>
      ) : (
        <DailySchedule
          actualComplianceWarnings={actualComplianceWarnings}
          printHoursShownEnabled={props.printHoursShownEnabled}
          openLiveAction={props.openLiveAction}
          showActualHours={showActualHours}
          handleChangeMetricsToggle={handleChangeMetricsToggle}
          weekHasStartedForActuals={props.weekHasStartedForActuals}
          liveLaborEnabled={props.liveLaborEnabled}
          homeCompany={props.homeCompany}
          dailyPlanEnabled={props.dailyPlanEnabled}
          actualTimesByPublishedId={actualTimesByPublishedId}
          coveredPublishedIds={coveredPublishedIds}
          coverageOwners={coverageOwners}
          actualCards={visibleActualCards}
          actualHoursByMember={hoursToDate.actual}
          scheduledHoursToDateByMember={hoursToDate.scheduled}
          actualHoursByMemberDate={hoursToDate.actualByDate}
          scheduledHoursByMemberDate={hoursToDate.scheduledByDate}
          publishedCoverageShifts={hoursToDate.publishedCoverageShifts}
          actualHoursAvailable={props.actualHoursAvailable}
          summaryRoleTypes={roleFilterActive ? props.roleType : undefined}
          storeBusinessDate={todayStr}
          openDirectMessageWith={props.openDirectMessageWith}
          currentUserId={props.currentUserId}
          openEmployeeProfile={props.openEmployeeProfile}
          toggleDailyWeekly={props.toggleDailyWeekly}
          dowSelected={props.dowSelected}
          get_date_info={get_date_info}
          get_person_shift_info={get_person_shift_info}
          store_team_member_weekly_info_view={
            props.store_team_member_weekly_info_view
          }
          store_team_member_weekly_info={props.store_team_member_weekly_info}
          lock_person={props.lock_person}
          role_names={props.all_data.role_names}
          all_data_full={props.all_data_full}
          addForecastAdjustment={props.addForecastAdjustment}
          addManualActualSales={props.addManualActualSales}
          addAdjustmentFinished={props.addAdjustmentFinished}
          lock_day={props.lock_day}
          day_of_week_str={day_of_week_str}
          week_in_past={props.week_in_past}
          updatePersonInfo={props.updatePersonInfo}
          get_t_str={props.get_t_str}
          lockAllShifts={props.lockAllShifts}
          displayMode={props.displayMode}
          saveShiftFinished={props.saveShiftFinished}
          setSaveShiftFinished={props.setSaveShiftFinished}
          setCopiedShift={props.setCopiedShift}
          addMultipleShifts={props.addMultipleShifts}
          addShift={props.addShift}
          store_id={props.store_id}
          agg_column={props.agg_column}
          calculate_letter={calculate_letter}
          calculate_color={calculate_color}
          combine_data={combine_data}
          copiedShift={props.copiedShift}
          edit_hoo={props.edit_hoo}
          saveEvent={props.saveEvent}
          updateEvent={props.updateEvent}
          deleteEvent={props.deleteEvent}
          eventAttachmentProps={props.eventAttachmentProps}
          saveEventFinished={props.saveEventFinished}
          setSaveEventFinished={props.setSaveEventFinished}
          saveForecastExcludeDate={props.saveForecastExcludeDate}
          deleteForecastExcludeDate={props.deleteForecastExcludeDate}
          saveBlackoutDate={props.saveBlackoutDate}
          deleteBlackoutDate={props.deleteBlackoutDate}
          blackout_dates={props.blackout_dates}
          dailyTemplateProps={props.dailyTemplateProps}
          filter_df_to_day={props.filter_df_to_day}
          weather={props.all_data.weather}
          events={props.all_data.events}
          local_events={props.all_data.local_events}
          forecast_exclude_dates={props.all_data.forecast_exclude_dates}
          role_types={props.all_data.role_types}
          businessWeekStartDayIndex={props.businessWeekStartDayIndex}
          start_qhnum={props.start_qhnum}
          schedule_day_end_qhnum={props.schedule_day_end_qhnum}
          storeTimezone={props.storeTimezone}
          frequentShifts={props.frequentShifts}
          post_missed_shift={props.post_missed_shift}
          showUnpublishedShifts={props.showUnpublishedShifts}
          defaultBreakLengthHours={props.defaultBreakLengthHours}
          week_already_started={props.week_already_started}
          editShiftsInPastEnabled={props.editShiftsInPastEnabled}
          highlightedEmployees={props.highlightedEmployees}
          isCopilotOn={props.isCopilotOn}
          proficiencyStarsCount={props.proficiencyStarsCount}
          proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
          calculate_daily_budget_score={calculate_daily_budget_score}
          calculate_intraday_coverage_score={calculate_intraday_coverage_score}
          batch_guidance={props.all_data.batch_guidance}
          intraday_metrics={props.all_data.intraday_metrics}
          actual_intraday_metrics={props.all_data.actual_intraday_metrics}
          reservation_summary={props.all_data.reservation_summary}
          breakRequiredWhenShiftLengthExceeds={
            props.breakRequiredWhenShiftLengthExceeds
          }
          secondBreakShiftLengthThreshold={
            props.secondBreakShiftLengthThreshold
          }
          open_shifts={open_shifts}
          laborModelEnabled={props.laborModelEnabled}
          laborLawViolations={props.laborLawViolations}
          laborLawEnabled={props.laborLawEnabled}
          paidBreakInputsEnabled={props.paidBreakInputsEnabled}
          businessDeclineShiftOptionEnabled={
            props.businessDeclineShiftOptionEnabled
          }
          restBreakRegulation={props.restBreakRegulation}
          manualSalesInputEnabled={props.manualSalesInputEnabled}
          refreshActualsForDate={props.refreshActualsForDate}
          pos_provider={props.pos_provider}
          windowWidth={props.windowWidth}
          checkedMetricsToggle={checkedMetricsToggle}
        />
      );
    }
  } else if (
    props.store_team_member_weekly_info_view.length === 0 &&
    !props.loading
  ) {
    return <NoStoreTeamMemberDisplay roleType={props.roleType} />;
  } else {
    return <SkeletonDisplay />;
  }
}

export default ScheduleDisplay2;
